import React from "react";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { useTranslation } from "../../hooks/useTranslation";
import { MEDIA_URL } from '../../config/constants';

const CamperCard = ({ vehicle, variant = "catalog" }) => {
  const navigate = useNavigate();
  const { getLocalizedPath, t } = useTranslation('catalog');

  const handleViewDetails = () => {
    // Track vehicle card click
    ReactGA.event({
      category: 'Vehicle Interaction',
      action: 'Card Click',
      label: `${variant}_${vehicle.vehicle_design || vehicle.title || 'unknown'}`,
      value: parseFloat(vehicle.price?.replace('$', '').replace('.00', '')) || 0
    });

    // Check vehicle design to determine the correct page
    // Pass vehicle ID via state so the detail page knows which specific vehicle to show
    if (vehicle.vehicle_design === 'Wave Catcher') {
      navigate(getLocalizedPath('/campervan/wave-catchers'), { state: { vehicleId: vehicle.id } });
    } else if (vehicle.vehicle_design === 'Sun Catcher') {
      navigate(getLocalizedPath('/campervan/sun-catchers'), { state: { vehicleId: vehicle.id } });
    } else if (vehicle.vehicle_design === 'Family Catcher') {
      navigate(getLocalizedPath('/campervan/family-catchers'), { state: { vehicleId: vehicle.id } });
    } else if (vehicle.id) {
      // Dynamic routing for future designs
      navigate(getLocalizedPath(`/campervan/${vehicle.id}`), { state: { vehicleId: vehicle.id } });
    } else if (vehicle.name || vehicle.title) {
      // Fallback: try to infer from name/title
      const name = (vehicle.name || vehicle.title).toLowerCase();
      if (name.includes('wave')) {
        navigate(getLocalizedPath('/campervan/wave-catchers'), { state: { vehicleId: vehicle.id } });
      } else if (name.includes('sun')) {
        navigate(getLocalizedPath('/campervan/sun-catchers'), { state: { vehicleId: vehicle.id } });
      } else if (name.includes('family')) {
        navigate(getLocalizedPath('/campervan/family-catchers'), { state: { vehicleId: vehicle.id } });
      } else {
        // Default fallback to Sun Catcher
        navigate(getLocalizedPath('/campervan/sun-catchers'), { state: { vehicleId: vehicle.id } });
      }
    } else {
      // Final fallback to Sun Catcher
      navigate(getLocalizedPath('/campervan/sun-catchers'));
    }
  };

  // Predefined features configuration per vehicle design
  const PREDEFINED_FEATURES = {
    'Sun Catcher': [
      { match: /Hot Water.*Boiler/i, fallback: 'Hot Water Boiler' },
      { match: /Bunk Bed/i, fallback: 'Bunk Bed' },
      { match: /Ventilation/i, fallback: 'Ventilation 3x' },
      { match: /Solar/i, fallback: 'Solar Panels' }
    ],
    'Wave Catcher': [
      { match: /Hot Water.*Boiler/i, fallback: 'Hot Water Boiler' },
      { match: /Pop-up Roof Bed/i, fallback: 'Pop-up Roof Bed' },
      { match: /Diesel Heater/i, fallback: 'Diesel Heater' },
      { match: /Solar/i, fallback: 'Solar Panels' }
    ],
    'Family Catcher': [
      { match: /Hot Water.*Boiler/i, fallback: 'Hot Water Boiler' },
      { match: /Pop-up Roof Bed/i, fallback: 'Pop-up Roof Bed' },
      { match: /Diesel Heater/i, fallback: 'Diesel Heater' },
      { match: /Solar/i, fallback: 'Solar Panels' }
    ]
  };

  const getFeatureIcon = (feature) => {
    const featureText = typeof feature === 'string' ? feature : feature.text;
    if (featureText.includes("Kitchen")) {
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_325_1137)">
            <path
              d="M15.8597 6.3125C15.8023 6.23232 15.7297 6.16425 15.6459 6.11218C15.5622 6.0601 15.469 6.02505 15.3717 6.00903C15.2744 5.99301 15.1749 5.99633 15.0789 6.0188C14.9829 6.04128 14.8923 6.08246 14.8122 6.14L14.2497 6.5425V5.5C14.2497 5.30109 14.1707 5.11032 14.03 4.96967C13.8894 4.82902 13.6986 4.75 13.4997 4.75H2.49969C2.30078 4.75 2.11002 4.82902 1.96936 4.96967C1.82871 5.11032 1.74969 5.30109 1.74969 5.5V6.5425L1.18719 6.14C1.10709 6.08255 1.01645 6.04143 0.920454 6.01901C0.824459 5.99658 0.724987 5.99329 0.627718 6.00931C0.530449 6.02532 0.437287 6.06034 0.353551 6.11237C0.269816 6.16439 0.197147 6.23239 0.139694 6.3125C0.0822407 6.39261 0.041128 6.48324 0.0187035 6.57924C-0.00372111 6.67523 -0.00701848 6.77471 0.00899963 6.87198C0.0413497 7.06842 0.150412 7.24397 0.312194 7.36L1.74969 8.38625V12C1.74969 12.5967 1.98675 13.169 2.4087 13.591C2.83066 14.0129 3.40296 14.25 3.99969 14.25H11.9997C12.5964 14.25 13.1687 14.0129 13.5907 13.591C14.0126 13.169 14.2497 12.5967 14.2497 12V8.38625L15.6872 7.36C15.7674 7.3026 15.8354 7.22996 15.8875 7.14623C15.9396 7.06249 15.9746 6.96932 15.9907 6.87202C16.0067 6.77473 16.0034 6.67523 15.9809 6.57922C15.9584 6.48321 15.9172 6.39258 15.8597 6.3125ZM12.7497 12C12.7497 12.1989 12.6707 12.3897 12.53 12.5303C12.3894 12.671 12.1986 12.75 11.9997 12.75H3.99969C3.80078 12.75 3.61002 12.671 3.46936 12.5303C3.32871 12.3897 3.24969 12.1989 3.24969 12V6.25H12.7497V12ZM4.74969 3V1.5C4.74969 1.30109 4.82871 1.11032 4.96936 0.96967C5.11002 0.829018 5.30078 0.75 5.49969 0.75C5.69861 0.75 5.88937 0.829018 6.03002 0.96967C6.17068 1.11032 6.24969 1.30109 6.24969 1.5V3C6.24969 3.19891 6.17068 3.38968 6.03002 3.53033C5.88937 3.67098 5.69861 3.75 5.49969 3.75C5.30078 3.75 5.11002 3.67098 4.96936 3.53033C4.82871 3.38968 4.74969 3.19891 4.74969 3ZM7.24969 3V1.5C7.24969 1.30109 7.32871 1.11032 7.46936 0.96967C7.61002 0.829018 7.80078 0.75 7.99969 0.75C8.19861 0.75 8.38937 0.829018 8.53002 0.96967C8.67068 1.11032 8.74969 1.30109 8.74969 1.5V3C8.74969 3.19891 8.67068 3.38968 8.53002 3.53033C8.38937 3.67098 8.19861 3.75 7.99969 3.75C7.80078 3.75 7.61002 3.67098 7.46936 3.53033C7.32871 3.38968 7.24969 3.19891 7.24969 3ZM9.74969 3V1.5C9.74969 1.30109 9.82871 1.11032 9.96936 0.96967C10.11 0.829018 10.3008 0.75 10.4997 0.75C10.6986 0.75 10.8894 0.829018 11.03 0.96967C11.1707 1.11032 11.2497 1.30109 11.2497 1.5V3C11.2497 3.19891 11.1707 3.38968 11.03 3.53033C10.8894 3.67098 10.6986 3.75 10.4997 3.75C10.3008 3.75 10.11 3.67098 9.96936 3.53033C9.82871 3.38968 9.74969 3.19891 9.74969 3Z"
              fill="#009B31"
            />
          </g>
        </svg>
      );
    } else if (featureText === "Solar-panels" || featureText.includes("Solar")) {
      // Sun icon for solar panels
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="8" cy="8.5" r="4" fill="#009B31"/>
          <path d="M8 1.5V3.5M8 13.5V15.5M1 8.5H3M13 8.5H15M2.93 3.43L4.34 4.84M11.66 12.16L13.07 13.57M2.93 13.57L4.34 12.16M11.66 4.84L13.07 3.43" stroke="#009B31" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    } else if (featureText === "WC" || featureText.includes("WC") || featureText.includes("Toilet")) {
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.5 3.5C13.5 2.83696 13.2366 2.20107 12.7678 1.73223C12.2989 1.26339 11.663 1 11 1H5C4.33696 1 3.70107 1.26339 3.23223 1.73223C2.76339 2.20107 2.5 2.83696 2.5 3.5V14.5C2.5 14.7652 2.60536 15.0196 2.79289 15.2071C2.98043 15.3946 3.23478 15.5 3.5 15.5H12.5C12.7652 15.5 13.0196 15.3946 13.2071 15.2071C13.3946 15.0196 13.5 14.7652 13.5 14.5V3.5ZM5 2.5H11C11.2652 2.5 11.5196 2.60536 11.7071 2.79289C11.8946 2.98043 12 3.23478 12 3.5V13.5H4V3.5C4 3.23478 4.10536 2.98043 4.29289 2.79289C4.48043 2.60536 4.73478 2.5 5 2.5ZM10 5.5H6C5.86739 5.5 5.74021 5.55268 5.64645 5.64645C5.55268 5.74021 5.5 5.86739 5.5 6C5.5 6.13261 5.55268 6.25979 5.64645 6.35355C5.74021 6.44732 5.86739 6.5 6 6.5H10C10.1326 6.5 10.2598 6.44732 10.3536 6.35355C10.4473 6.25979 10.5 6.13261 10.5 6C10.5 5.86739 10.4473 5.74021 10.3536 5.64645C10.2598 5.55268 10.1326 5.5 10 5.5ZM8 8.5C7.86739 8.5 7.74021 8.55268 7.64645 8.64645C7.55268 8.74021 7.5 8.86739 7.5 9V10C7.5 10.1326 7.55268 10.2598 7.64645 10.3536C7.74021 10.4473 7.86739 10.5 8 10.5C8.13261 10.5 8.25979 10.4473 8.35355 10.3536C8.44732 10.2598 8.5 10.1326 8.5 10V9C8.5 8.86739 8.44732 8.74021 8.35355 8.64645C8.25979 8.55268 8.13261 8.5 8 8.5Z"
            fill="#009B31"
          />
        </svg>
      );
    } else if (featureText.includes("Sleeping Areas")) {
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.25 4.75H2.25V3.5C2.25 3.30109 2.17098 3.11032 2.03033 2.96967C1.88968 2.82902 1.69891 2.75 1.5 2.75C1.30109 2.75 1.11032 2.82902 0.96967 2.96967C0.829018 3.11032 0.75 3.30109 0.75 3.5V13.5C0.75 13.6989 0.829018 13.8897 0.96967 14.0303C1.11032 14.171 1.30109 14.25 1.5 14.25C1.69891 14.25 1.88968 14.171 2.03033 14.0303C2.17098 13.8897 2.25 13.6989 2.25 13.5V11.75H14.5V13.5C14.5 13.6989 14.579 13.8897 14.7197 14.0303C14.8603 14.171 15.0511 14.25 15.25 14.25C15.4489 14.25 15.6397 14.171 15.7803 14.0303C15.921 13.8897 16 13.6989 16 13.5V7.5C15.9992 6.77091 15.7092 6.07192 15.1936 5.55637C14.6781 5.04082 13.9791 4.75083 13.25 4.75ZM6.25 10.25H2.25V6.25H6.25V10.25ZM14.5 10.25H7.75V6.25H13.25C13.5815 6.25 13.8995 6.3817 14.1339 6.61612C14.3683 6.85054 14.5 7.16848 14.5 7.5V10.25Z"
            fill="#009B31"
          />
        </svg>
      );
    } else if (featureText.includes("WiFi") || featureText.includes("Wi-Fi")) {
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.99972 13.25C8.99972 13.4478 8.94107 13.6411 8.83119 13.8056C8.72131 13.97 8.56513 14.0982 8.38241 14.1739C8.19968 14.2496 7.99861 14.2694 7.80463 14.2308C7.61065 14.1922 7.43247 14.0969 7.29262 13.9571C7.15276 13.8172 7.05752 13.6391 7.01894 13.4451C6.98035 13.2511 7.00016 13.05 7.07584 12.8673C7.15153 12.6846 7.2797 12.5284 7.44415 12.4185C7.6086 12.3086 7.80194 12.25 7.99972 12.25C8.26494 12.25 8.51929 12.3553 8.70683 12.5429C8.89437 12.7304 8.99972 12.9848 8.99972 13.25ZM14.9753 5.74436C13.0085 4.13128 10.5434 3.24969 7.99972 3.24969C5.45602 3.24969 2.99092 4.13128 1.0241 5.74436C0.947973 5.80686 0.884903 5.88375 0.83849 5.97062C0.792078 6.0575 0.763231 6.15266 0.753597 6.25069C0.743962 6.34871 0.75373 6.44767 0.782341 6.54192C0.810953 6.63617 0.857848 6.72386 0.920349 6.79999C0.98285 6.87611 1.05973 6.93918 1.14661 6.9856C1.23348 7.03201 1.32865 7.06086 1.42667 7.07049C1.5247 7.08013 1.62366 7.07036 1.71791 7.04175C1.81216 7.01313 1.89985 6.96624 1.97597 6.90374C3.6745 5.51095 5.80317 4.74979 7.99972 4.74979C10.1963 4.74979 12.325 5.51095 14.0235 6.90374C14.0996 6.96624 14.1873 7.01313 14.2815 7.04175C14.3758 7.07036 14.4747 7.08013 14.5728 7.07049C14.6708 7.06086 14.766 7.03201 14.8528 6.9856C14.9397 6.93918 15.0166 6.87611 15.0791 6.79999C15.1416 6.72386 15.1885 6.63617 15.2171 6.54192C15.2457 6.44767 15.2555 6.34871 15.2459 6.25069C15.2362 6.15266 15.2074 6.0575 15.161 5.97062C15.1145 5.88375 15.0515 5.80686 14.9753 5.74436ZM12.9653 7.97749C11.552 6.85862 9.8023 6.24985 7.99972 6.24985C6.19715 6.24985 4.4474 6.85862 3.0341 7.97749C2.88209 8.10226 2.78517 8.28174 2.76421 8.47727C2.74325 8.67281 2.79993 8.86875 2.92204 9.0229C3.04415 9.17706 3.22192 9.27708 3.41706 9.30144C3.6122 9.32579 3.8091 9.27253 3.96535 9.15311C5.11362 8.24413 6.53522 7.74957 7.99972 7.74957C9.46423 7.74957 10.8858 8.24413 12.0341 9.15311C12.1903 9.27253 12.3872 9.32579 12.5824 9.30144C12.7775 9.27708 12.9553 9.17706 13.0774 9.0229C13.1995 8.86875 13.2562 8.67281 13.2352 8.47727C13.2143 8.28174 13.1174 8.10226 12.9653 7.97749ZM10.9416 10.2062C10.0866 9.5847 9.05675 9.24992 7.99972 9.24992C6.9427 9.24992 5.91283 9.5847 5.05785 10.2062C4.89706 10.3234 4.78941 10.4997 4.75859 10.6963C4.72776 10.8928 4.77628 11.0936 4.89347 11.2544C5.01067 11.4152 5.18693 11.5228 5.38349 11.5536C5.58005 11.5845 5.78081 11.5359 5.9416 11.4187C6.53973 10.9839 7.26023 10.7497 7.99972 10.7497C8.73922 10.7497 9.45972 10.9839 10.0578 11.4187C10.1375 11.4768 10.2277 11.5185 10.3235 11.5417C10.4192 11.5648 10.5186 11.5689 10.616 11.5536C10.7133 11.5384 10.8066 11.5041 10.8907 11.4527C10.9748 11.4014 11.0479 11.334 11.106 11.2544C11.164 11.1748 11.2058 11.0845 11.2289 10.9887C11.2521 10.893 11.2561 10.7936 11.2409 10.6963C11.2256 10.5989 11.1913 10.5056 11.14 10.4215C11.0886 10.3374 11.0212 10.2643 10.9416 10.2062Z"
            fill="#009B31"
          />
        </svg>
      );
    } else if (featureText.includes("Hot Water") || featureText.includes("Boiler")) {
      // Water heater icon
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2.5H4C3.44772 2.5 3 2.94772 3 3.5V13.5C3 14.0523 3.44772 14.5 4 14.5H12C12.5523 14.5 13 14.0523 13 13.5V3.5C13 2.94772 12.5523 2.5 12 2.5ZM8 12.5C6.89543 12.5 6 11.6046 6 10.5C6 9.5 7 8 8 6.5C9 8 10 9.5 10 10.5C10 11.6046 9.10457 12.5 8 12.5ZM11 5.5H5V4.5H11V5.5Z"
            fill="#009B31"
          />
        </svg>
      );
    } else if (featureText.includes("Pop-up Roof Bed") || featureText.includes("Roof Bed")) {
      // Roof bed icon (elevated bed)
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.25 4.75H2.25V3.5C2.25 3.30109 2.17098 3.11032 2.03033 2.96967C1.88968 2.82902 1.69891 2.75 1.5 2.75C1.30109 2.75 1.11032 2.82902 0.96967 2.96967C0.829018 3.11032 0.75 3.30109 0.75 3.5V13.5C0.75 13.6989 0.829018 13.8897 0.96967 14.0303C1.11032 14.171 1.30109 14.25 1.5 14.25C1.69891 14.25 1.88968 14.171 2.03033 14.0303C2.17098 13.8897 2.25 13.6989 2.25 13.5V11.75H14.5V13.5C14.5 13.6989 14.579 13.8897 14.7197 14.0303C14.8603 14.171 15.0511 14.25 15.25 14.25C15.4489 14.25 15.6397 14.171 15.7803 14.0303C15.921 13.8897 16 13.6989 16 13.5V7.5C15.9992 6.77091 15.7092 6.07192 15.1936 5.55637C14.6781 5.04082 13.9791 4.75083 13.25 4.75ZM6.25 10.25H2.25V6.25H6.25V10.25ZM14.5 10.25H7.75V6.25H13.25C13.5815 6.25 13.8995 6.3817 14.1339 6.61612C14.3683 6.85054 14.5 7.16848 14.5 7.5V10.25Z"
            fill="#009B31"
          />
        </svg>
      );
    } else if (featureText.includes("Bunk Bed")) {
      // Bunk bed icon (stacked beds)
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 2.5H2C1.44772 2.5 1 2.94772 1 3.5V4.5C1 5.05228 1.44772 5.5 2 5.5H14C14.5523 5.5 15 5.05228 15 4.5V3.5C15 2.94772 14.5523 2.5 14 2.5Z"
            fill="#009B31"
          />
          <path
            d="M14 7.5H2C1.44772 7.5 1 7.94772 1 8.5V9.5C1 10.0523 1.44772 10.5 2 10.5H14C14.5523 10.5 15 10.0523 15 9.5V8.5C15 7.94772 14.5523 7.5 14 7.5Z"
            fill="#009B31"
          />
          <path
            d="M2 5.5V7.5M14 5.5V7.5M2 10.5V14.5M14 10.5V14.5"
            stroke="#009B31"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      );
    } else if (featureText.includes("Diesel Heater") || featureText.includes("Heater")) {
      // Heater/flame icon (larger)
      return (
        <svg
          width="18"
          height="19"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 2.5C8 2.5 5.5 5.5 5.5 9C5.5 11.5 6.5 13.5 8 14.5C9.5 13.5 10.5 11.5 10.5 9C10.5 5.5 8 2.5 8 2.5ZM8 12.5C7 12 6.5 10.5 6.5 9C6.5 7 7.5 5 8 4C8.5 5 9.5 7 9.5 9C9.5 10.5 9 12 8 12.5Z"
            fill="#009B31"
          />
          <path
            d="M8 6.5C8 6.5 7 8 7 9.5C7 10.5 7.5 11.5 8 11.5C8.5 11.5 9 10.5 9 9.5C9 8 8 6.5 8 6.5Z"
            fill="#009B31"
          />
        </svg>
      );
    } else if (featureText.includes("Ventilation") || featureText.includes("Fan")) {
      // Fan/ventilation icon
      return (
        <svg
          width="16"
          height="17"
          viewBox="0 0 16 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 9.5C8.55228 9.5 9 9.05228 9 8.5C9 7.94772 8.55228 7.5 8 7.5C7.44772 7.5 7 7.94772 7 8.5C7 9.05228 7.44772 9.5 8 9.5Z"
            fill="#009B31"
          />
          <path
            d="M8 6.5C8.5 6.5 9.5 6 10.5 4.5C11.5 3 11 1.5 11 1.5C11 1.5 9.5 2 8 2C6.5 2 5 1.5 5 1.5C5 1.5 4.5 3 5.5 4.5C6.5 6 7.5 6.5 8 6.5Z"
            fill="#009B31"
          />
          <path
            d="M10.5 8.5C10.5 8 11 7 12.5 6C14 5 15.5 5.5 15.5 5.5C15.5 5.5 15 7 15 8.5C15 10 15.5 11.5 15.5 11.5C15.5 11.5 14 12 12.5 11C11 10 10.5 9 10.5 8.5Z"
            fill="#009B31"
          />
          <path
            d="M8 10.5C7.5 10.5 6.5 11 5.5 12.5C4.5 14 5 15.5 5 15.5C5 15.5 6.5 15 8 15C9.5 15 11 15.5 11 15.5C11 15.5 11.5 14 10.5 12.5C9.5 11 8.5 10.5 8 10.5Z"
            fill="#009B31"
          />
          <path
            d="M5.5 8.5C5.5 9 5 10 3.5 11C2 12 0.5 11.5 0.5 11.5C0.5 11.5 1 10 1 8.5C1 7 0.5 5.5 0.5 5.5C0.5 5.5 2 5 3.5 6C5 7 5.5 8 5.5 8.5Z"
            fill="#009B31"
          />
        </svg>
      );
    }

    // Default icon if no match
    return (
      <svg
        width="16"
        height="17"
        viewBox="0 0 16 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M13.25 4.75H2.25V3.5C2.25 3.30109 2.17098 3.11032 2.03033 2.96967C1.88968 2.82902 1.69891 2.75 1.5 2.75C1.30109 2.75 1.11032 2.82902 0.96967 2.96967C0.829018 3.11032 0.75 3.30109 0.75 3.5V13.5C0.75 13.6989 0.829018 13.8897 0.96967 14.0303C1.11032 14.171 1.30109 14.25 1.5 14.25C1.69891 14.25 1.88968 14.171 2.03033 14.0303C2.17098 13.8897 2.25 13.6989 2.25 13.5V11.75H14.5V13.5C14.5 13.6989 14.579 13.8897 14.7197 14.0303C14.8603 14.171 15.0511 14.25 15.25 14.25C15.4489 14.25 15.6397 14.171 15.7803 14.0303C15.921 13.8897 16 13.6989 16 13.5V7.5C15.9992 6.77091 15.7092 6.07192 15.1936 5.55637C14.6781 5.04082 13.9791 4.75083 13.25 4.75ZM6.25 10.25H2.25V6.25H6.25V10.25ZM14.5 10.25H7.75V6.25H13.25C13.5815 6.25 13.8995 6.3817 14.1339 6.61612C14.3683 6.85054 14.5 7.16848 14.5 7.5V10.25Z"
          fill="#009B31"
        />
      </svg>
    );
  };

  // Get predefined features based on vehicle design
  function getPredefinedFeatures(vehicleData) {
    const design = vehicleData.vehicle_design || 'Sun Catcher';
    const config = PREDEFINED_FEATURES[design] || PREDEFINED_FEATURES['Sun Catcher'];
    const features = vehicleData.features || [];

    return config.map(({ match, fallback }) => {
      // Find matching feature from DB
      const found = features.find(f => {
        const text = typeof f === 'string' ? f : f.text;
        return text && match.test(text);
      });

      if (found) {
        return typeof found === 'string' ? found : found.text;
      }
      return fallback;
    });
  }

  // Get predefined 4 features for both homepage and catalog variants
  const displayFeatures = getPredefinedFeatures(vehicle);

  // Use thumbnails for card display
  const getVehicleImage = () => {
    const name = (vehicle.name || vehicle.title || '').toLowerCase();
    if (vehicle.vehicle_design === 'Wave Catcher' || name.includes('wave') || name.includes('swell')) {
      return `${MEDIA_URL}/images/thumbnails/WaveCatcher0001-thumbnail.webp`;
    }
    if (vehicle.vehicle_design === 'Sun Catcher' || name.includes('sun catcher')) {
      return `${MEDIA_URL}/images/thumbnails/Sun-Catcher-Campervan-thumbnail.webp`;
    }
    if (vehicle.vehicle_design === 'Family Catcher' || name.includes('family')) {
      return `${MEDIA_URL}/images/thumbnails/FamilyCatcher0001-thumbnail.webp`;
    }
    return vehicle.image || vehicle.featuredImageUrl;
  };

  return (
    <div className={`camper-card ${variant === "homepage" ? "van-card" : "product-card"}`}>
      <div className={variant === "homepage" ? "van-image-container" : "product-image"}>
        <img
          src={getVehicleImage()}
          className={variant === "homepage" ? "van-image" : ""}
          alt={vehicle.title || vehicle.name}
          loading="lazy"
        />
        <div className={variant === "homepage" ? "pagination-dots" : "image-pagination"}>
          <div className="pagination-dot"></div>
          {variant === "catalog" && (
            <>
              <div className="pagination-dot"></div>
              <div className="pagination-dot"></div>
            </>
          )}
        </div>
      </div>

      <div className={variant === "homepage" ? "van-info" : "product-info"}>
        <div className={variant === "homepage" ? "van-header" : "product-header"}>
          <div className="van-title-price">
            <div className="van-title">{vehicle.title || vehicle.name}</div>
            <div className="van-price-container">
              <div className="starting-from">{t('card.startingFrom')} </div>
              <div className="van-price">{vehicle.price}</div>
            </div>
          </div>
          <div className="van-description">{vehicle.description}</div>
        </div>

        <div className={variant === "homepage" ? "van-features" : "product-features"}>
          {displayFeatures.map((feature, index) => (
            <div key={index} className="feature-tag">
              <div className="feature-icon">{getFeatureIcon(feature)}</div>
              <div className="feature-text">{typeof feature === 'string' ? feature : feature.text}</div>
            </div>
          ))}
        </div>

        <div className={variant === "homepage" ? "van-actions" : "product-actions"}>
          <div className={variant === "homepage" ? "van-capacity" : "capacity-info"}>
            <div className="capacity-icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.82348 9.80876C8.45164 9.28076 8.90221 8.57235 9.11409 7.77958C9.32597 6.98682 9.28891 6.14807 9.00792 5.37709C8.72693 4.60611 8.21563 3.9402 7.54334 3.46967C6.87105 2.99914 6.07032 2.74677 5.24973 2.74677C4.42914 2.74677 3.62841 2.99914 2.95612 3.46967C2.28383 3.9402 1.77253 4.60611 1.49154 5.37709C1.21056 6.14807 1.17349 6.98682 1.38537 7.77958C1.59725 8.57235 2.04782 9.28076 2.67598 9.80876C1.69509 10.2523 0.845054 10.9411 0.207856 11.8088C0.0901665 11.9691 0.0410049 12.1697 0.0711866 12.3663C0.101368 12.5629 0.208421 12.7395 0.368794 12.8572C0.529167 12.9749 0.729724 13.024 0.926344 12.9939C1.12296 12.9637 1.29954 12.8566 1.41723 12.6963C1.85832 12.0938 2.43521 11.6039 3.10109 11.2662C3.76697 10.9284 4.50309 10.7524 5.24973 10.7524C5.99637 10.7524 6.73249 10.9284 7.39837 11.2662C8.06425 11.6039 8.64114 12.0938 9.08223 12.6963C9.19992 12.8567 9.37653 12.9638 9.57321 12.9941C9.76989 13.0243 9.97052 12.9752 10.131 12.8575C10.2914 12.7398 10.3986 12.5632 10.4288 12.3665C10.459 12.1699 10.4099 11.9692 10.2922 11.8088C9.65465 10.9412 8.80445 10.2524 7.82348 9.80876ZM2.74973 6.75001C2.74973 6.25556 2.89635 5.77221 3.17106 5.36108C3.44576 4.94996 3.83621 4.62953 4.29302 4.44031C4.74984 4.25109 5.2525 4.20158 5.73746 4.29805C6.22241 4.39451 6.66787 4.63261 7.0175 4.98224C7.36713 5.33187 7.60523 5.77733 7.70169 6.26228C7.79816 6.74724 7.74865 7.2499 7.55943 7.70672C7.37021 8.16353 7.04978 8.55398 6.63866 8.82868C6.22753 9.10339 5.74418 9.25001 5.24973 9.25001C4.58669 9.25001 3.95081 8.98662 3.48196 8.51778C3.01312 8.04894 2.74973 7.41305 2.74973 6.75001ZM15.631 12.8544C15.5516 12.9127 15.4615 12.9549 15.3658 12.9784C15.2701 13.0019 15.1707 13.0063 15.0733 12.9914C14.9759 12.9765 14.8824 12.9425 14.7982 12.8914C14.7139 12.8404 14.6405 12.7732 14.5822 12.6938C14.1401 12.0925 13.5629 11.6034 12.8973 11.2658C12.2317 10.9282 11.4961 10.7515 10.7497 10.75C10.5508 10.75 10.3601 10.671 10.2194 10.5303C10.0787 10.3897 9.99973 10.1989 9.99973 10C9.99973 9.8011 10.0787 9.61033 10.2194 9.46968C10.3601 9.32903 10.5508 9.25001 10.7497 9.25001C11.1051 9.24938 11.4562 9.173 11.7797 9.02598C12.1032 8.87895 12.3916 8.66465 12.6258 8.39737C12.8599 8.13009 13.0344 7.81597 13.1376 7.47594C13.2407 7.13591 13.2702 6.7778 13.2241 6.42547C13.178 6.07314 13.0573 5.73469 12.87 5.43269C12.6828 5.13068 12.4333 4.87206 12.1383 4.67407C11.8432 4.47607 11.5093 4.34326 11.1589 4.28447C10.8084 4.22568 10.4495 4.24227 10.106 4.33313C10.01 4.36089 9.90951 4.36921 9.81031 4.35759C9.71111 4.34597 9.61523 4.31466 9.5283 4.26548C9.44136 4.21631 9.36512 4.15027 9.30406 4.07123C9.24299 3.99219 9.19833 3.90176 9.17268 3.80523C9.14704 3.70869 9.14094 3.60801 9.15473 3.50909C9.16853 3.41017 9.20195 3.315 9.25302 3.22917C9.30409 3.14333 9.37179 3.06857 9.45215 3.00925C9.53251 2.94994 9.62391 2.90727 9.72098 2.88376C10.5994 2.65101 11.5309 2.7243 12.3621 3.09155C13.1933 3.45881 13.8747 4.09815 14.294 4.90431C14.7134 5.71048 14.8458 6.63542 14.6694 7.52685C14.493 8.41829 14.0183 9.22309 13.3235 9.80876C14.3044 10.2523 15.1544 10.9411 15.7916 11.8088C15.9085 11.9689 15.9571 12.1688 15.9271 12.3647C15.897 12.5607 15.7905 12.7367 15.631 12.8544Z"
                  fill="#009B31"
                />
              </svg>
            </div>
            <div className="capacity-text">{t('card.upTo')} {vehicle.sleeping_capacity || vehicle.sleepingCapacity || vehicle.capacity || 4} {t('card.people')}</div>
          </div>
          <button
            className={variant === "homepage" ? "view-details-button" : "view-details-btn"}
            onClick={handleViewDetails}
          >
            <div className={variant === "homepage" ? "button-state-layer" : "btn-text"}>{t('card.viewDetails')}</div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CamperCard;